import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

export default function AuthGuard({ children, requiredPermissions = [] }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user, checkAuth, initialized } = useAuthStore();
  const location = useLocation();

  // Validate the session exactly once for the whole app. Nested guards must not
  // re-trigger this, or the shared isLoading toggle causes a mount/unmount loop.
  useEffect(() => {
    if (!initialized) checkAuth();
  }, [initialized, checkAuth]);

  // Wait for the one-time auth check before deciding to render or redirect.
  if (!initialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check required permissions
  if (requiredPermissions.length > 0 && user) {
    const hasPermission = requiredPermissions.some(permission => 
      user.permissions.includes(permission) || 
      user.permissions.includes('admin:*') ||
      user.roles.includes('super_admin')
    );

    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
